import { BellRing, Briefcase, CalendarCheck, ClipboardList, Clock, FileText, Gauge, Headset, ListChecks, MapPin, ReceiptPoundSterling, Repeat, ShieldCheck, Star, Ruler, Timer, TrendingUp, Package, type LucideIcon } from 'lucide-react';

/**
 * Where each skill lives — slug, label, icon and the page it opens.
 *
 * Lifted out of Sidebar.tsx so it is not a component's private detail. The
 * skills marketplace needs the SAME slug → href mapping to make an installed
 * card open its page, and copying the hrefs into the marketplace would have
 * made a third hand-maintained list of the same facts. That pattern already
 * bit us once: the Service Reminders nav entry was missing from MobileMenu for
 * a month after the skill shipped, because the desktop and mobile lists are
 * hand-mirrored.
 *
 * MobileMenu still keeps its own copy (a different shape, with its own icon
 * choices) — worth folding in here next, but out of scope for the change that
 * moved this.
 */

export interface SkillNavItem {
  slug: string;
  category: string;
  label: string;
  href: string;
  activePage: string;
  icon: LucideIcon;
  badgeKey?: string;
  /**
   * REQUIRED, not optional — see the block comment on SKILL_NAV_LIST. It was
   * optional until 2026-08-14 and seven entries simply didn't set one, which
   * is a typo-shaped hole rather than a decision anyone made. Making it
   * mandatory turns "forgot to gate the new skill" into a compile error, which
   * is the only version of this rule that survives the fifteenth skill.
   *
   * An ARRAY means "any ONE of these is enough", never "all of them". Only
   * Timesheets needs it so far (`view_all_hours` or `view_own_hours`), and
   * any-of is the safe reading for a gate: an all-of list would silently hide a
   * page the moment someone added a key to it, which is the same shape of
   * accident this field exists to stop.
   */
  permission: string | readonly string[];
}

/** True if the viewer holds ANY of an entry's permissions. */
export function hasNavPermission(
  permission: string | readonly string[],
  holds: (key: string) => boolean,
): boolean {
  return typeof permission === 'string' ? holds(permission) : permission.some(holds);
}

/**
 * 🚨 EVERY entry needs a `permission`. A skill nav item without one renders for
 * every role in the account, and hiding a link was never the guard — but the
 * link is what tells someone the page is there to type at.
 *
 * Seven entries went without one until 2026-08-14 (quotes, invoicing, live-map,
 * work-orders, projects, reviews, revenue-reports), so a SUBCONTRACTOR — an
 * outside firm whose role has `view_quotes: false`, `view_invoices: false`,
 * `view_clients: false` sitting right there in the database — was shown the
 * lot, badge counts and all. Five of the seven had a perfectly good permission
 * key already defined in `SKILL_PERMISSIONS` that nobody had wired up here;
 * live-map and revenue-reports had no key in the system at all.
 *
 * `skill-nav-permissions.test.ts` now fails the build on an entry with no
 * `permission`, so this cannot silently happen again to skill number fifteen.
 */
/**
 * Who gets the Parts row (and the page, which asks the same). Any ONE of:
 *
 *   view_all_jobs     the office, who order for every engineer's jobs
 *   manage_van_stock  whoever sets the vans up
 *   count_own_van     the engineer: the skill's own key for him, granted at
 *                     install whether or not van stock is switched on
 *
 * Not `view_own_jobs`, though "My parts" is scoped by it: a subcontractor holds
 * that key and none of these, and sees his parts on each job's Parts panel,
 * not on a page of the firm's.
 */
export const PARTS_NAV_PERMISSION = ['view_all_jobs', 'manage_van_stock', 'count_own_van'] as const;

export const SKILL_NAV_LIST: SkillNavItem[] = [
  { slug: 'quotes', category: 'Finance', label: 'Quotes', href: '/quotes', activePage: 'quotes', icon: FileText, badgeKey: 'quotes', permission: 'view_quotes' },
  { slug: 'invoicing', category: 'Finance', label: 'Invoicing', href: '/invoices', activePage: 'invoicing', icon: ReceiptPoundSterling, badgeKey: 'invoices', permission: 'view_invoices' },
  { slug: 'live-map', category: 'Operations', label: 'Live Map', href: '/map', activePage: 'map', icon: MapPin, permission: 'view_live_map' },
  { slug: 'work-orders', category: 'Trade', label: 'Work Orders', href: '/work-orders', activePage: 'work-orders', icon: ClipboardList, permission: 'view_work_orders' },
  { slug: 'gas-certificates', category: 'Trade', label: 'Certificates', href: '/gas-certs', activePage: 'gas-certs', icon: ShieldCheck, permission: 'view_gas_certs' },
  { slug: 'gas-calculators', category: 'Trade', label: 'Calculators', href: '/gas-calculators', activePage: 'gas-calculators', icon: Gauge, permission: 'use_gas_calculators' },
  // 🚨 `use_custom_forms`, not `manage_form_templates`. The page lists the forms
  // that have been FILLED IN, the way /gas-certs lists issued certificates, so
  // an engineer who fills them in has business on it; authoring lives behind a
  // Settings button inside, gated on the narrower key (Tom, 4 September).
  // Parts (Parts & Van Stock, PR 3): one row for both halves, shown whenever the
  // skill is installed. Van stock is a tab of it. Badged with parts to order
  // for a seat that sees every job, parts to pick up for one that sees its own.
  { slug: 'parts-tracker', category: 'Trade', label: 'Parts', href: '/parts', activePage: 'parts', icon: Package, badgeKey: 'parts', permission: PARTS_NAV_PERMISSION },
  { slug: 'form-builder', category: 'Trade', label: 'Forms', href: '/forms', activePage: 'forms', icon: ListChecks, permission: 'use_custom_forms' },
  // Either key: the engineer who does surveys and the office that reads them.
  { slug: 'installation-survey', category: 'Trade', label: 'Surveys', href: '/surveys', activePage: 'surveys', icon: Ruler, permission: ['view_surveys', 'create_surveys'] },
  { slug: 'projects', category: 'Operations', label: 'Projects', href: '/projects', activePage: 'projects', icon: Briefcase, permission: 'view_projects' },
  // Operations, not Finance: the owner comes here to see who worked, and the
  // money is a consequence of that.
  //
  // EITHER hours permission opens it. It was `view_all_hours` alone, on the
  // reasoning that "a subcontractor logging their own days reaches the page
  // through their own surface, not the skill nav" — and that surface turned out
  // to be the mobile bar's Hours tab, which exists only on the phone. So a
  // subbie who opened Mucka on a laptop had no way to their own hours at all
  // beyond typing the URL (Tom, 2026-08-14). The PAGE has always handled
  // own-only viewers; it was only the way in that was missing.
  { slug: 'timesheets', category: 'Operations', label: 'Timesheets', href: '/timesheets', activePage: 'timesheets', icon: Clock, permission: ['view_all_hours', 'view_own_hours'] },
  { slug: 'reviews', category: 'Operations', label: 'Reviews', href: '/reviews', activePage: 'reviews', icon: Star, permission: 'view_reviews' },
  { slug: 'service-reminders', category: 'Operations', label: 'Service Reminders', href: '/service-reminders', activePage: 'service-reminders', icon: BellRing, permission: 'view_service_reminders' },
  { slug: 'revenue-reports', category: 'Finance', label: 'Revenue', href: '/revenue-reports', activePage: 'revenue-reports', icon: TrendingUp, permission: 'view_revenue_reports' },
  // Finance, not Operations: the plan book is a revenue asset and the owner
  // will look for it next to the money.
  { slug: 'service-plans', category: 'Finance', label: 'Service Plans', href: '/service-plans', activePage: 'service-plans', icon: Repeat, permission: 'view_service_plans' },
  {
    // Visibility is gated by the `use_on_time_tracker` permission, chosen
    // per-role at install time via SkillPermissionsWizard. Owners on the
    // tools should keep this; pure back-office roles can be excluded.
    slug: 'on-time-tracker', category: 'Operations', label: 'On Time Tracker', href: '/on-time-tracker', activePage: 'on-time-tracker', icon: Timer, permission: 'use_on_time_tracker',
  },
  {
    // Owner/manager config surface (manage_voice_receptionist). Engineers
    // are excluded by default — see SKILL_PERMISSIONS.
    slug: 'voice-receptionist', category: 'Operations', label: 'Receptionist', href: '/voice-receptionist', activePage: 'voice-receptionist', icon: Headset, permission: 'manage_voice_receptionist',
  },
  {
    // The booking page's own screen: what it has taken, with setup in a sheet
    // over it. Without this entry the page exists and nothing points at it —
    // an owner would land there once from the install and never find it again.
    slug: 'online-booking', category: 'Operations', label: 'Online booking', href: '/online-booking', activePage: 'online-booking', icon: CalendarCheck, permission: 'manage_online_booking',
  },
];
export const CATEGORY_ORDER = ['Trade', 'Operations', 'Finance'];

/**
 * The page an installed skill opens, or null when it has no page of its own.
 *
 * Null is a real answer, not a gap: `whatsapp-assistant` and the accounting
 * integrations are configured in Settings and have no screen to land on, so
 * their marketplace cards must stay inert rather than navigate nowhere.
 */
export function skillPageHref(slug: string): string | null {
  return SKILL_NAV_LIST.find((s) => s.slug === slug)?.href ?? null;
}
